import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, ExternalLink } from 'lucide-react';
import { apiFetch } from '../utils/api';
import { formatAmount } from '../utils/formatters';
import { TagSelector } from './TagSelector';
import { useI18n } from '@/hooks/useI18n';

interface TaxCategory {
    id: string;
    code: string;
    name: string;
    account_number: string;
    iban: string;
}

interface Company {
    id: string;
    name: string;
}

interface Tag {
    id: string;
    name: string;
    color?: string;
    icon?: string;
}

interface PaymentRow {
    id?: string;  // tax_payments record ID (undefined for new rows)
    tax_category_id: string;
    deadline: string;
    amount: string;
    tags: Tag[];
    payment_transaction_id?: string;
}

interface TaxBatchModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: () => void;
    batchId?: string;
}

export function TaxBatchModal({ isOpen, onClose, onSave, batchId }: TaxBatchModalProps) {
    const { t } = useI18n();
    const [year, setYear] = useState<number>(new Date().getFullYear());
    const [month, setMonth] = useState<number>(new Date().getMonth() + 1);
    const [companyId, setCompanyId] = useState<string>('');
    const [notes, setNotes] = useState<string>('');
    const [payments, setPayments] = useState<PaymentRow[]>([]);

    const [taxCategories, setTaxCategories] = useState<TaxCategory[]>([]);
    const [companies, setCompanies] = useState<Company[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [baseCurrency, setBaseCurrency] = useState<string>('HUF');

    useEffect(() => {
        if (isOpen) {
            fetchUserData();
            fetchCompanies();
            fetchTaxCategories(year);

            if (batchId) {
                fetchBatchData();
            } else {
                const today = new Date();
                setYear(today.getFullYear());
                setMonth(today.getMonth() + 1);
                setCompanyId('');
                setNotes('');
                setPayments([]);
            }
        }
    }, [isOpen, batchId]);

    // Refetch tax categories when year changes
    useEffect(() => {
        if (isOpen && year) {
            fetchTaxCategories(year);
        }
    }, [year, isOpen]);

    const fetchUserData = async () => {
        try {
            const response = await apiFetch('/api/auth/me');
            if (response.ok) {
                const data = await response.json();
                setBaseCurrency(data.base_currency || 'HUF');
            }
        } catch (error) {
            console.error('Error fetching user data:', error);
        }
    };

    const fetchCompanies = async () => {
        try {
            const response = await apiFetch('/api/companies/?limit=100');
            if (response.ok) {
                const data = await response.json();
                // Companies endpoint returns array directly, not wrapped in items
                setCompanies(Array.isArray(data) ? data : []);
            }
        } catch (error) {
            console.error('Error fetching companies:', error);
        }
    };

    const fetchTaxCategories = async (selectedYear: number) => {
        try {
            const response = await apiFetch(`/api/tax-categories/?year=${selectedYear}&limit=200`);
            if (response.ok) {
                const data = await response.json();
                setTaxCategories(Array.isArray(data) ? data : []);
            }
        } catch (error) {
            console.error('Error fetching tax categories:', error);
        }
    };

    const fetchBatchData = async () => {
        if (!batchId) return;

        setLoading(true);
        try {
            const response = await apiFetch(`/api/finance/tax-batches/${batchId}`);
            if (response.ok) {
                const data = await response.json();
                setYear(data.year);
                setMonth(data.month);
                setCompanyId(data.company_id);
                setNotes(data.notes || '');

                const paymentRows = data.payments.map((p: any) => ({
                    id: p.id,
                    tax_category_id: p.tax_category_id,
                    deadline: p.deadline,
                    amount: p.amount?.toString() || '0',
                    tags: p.tags || [],
                    payment_transaction_id: p.payment_transaction_id
                }));
                setPayments(paymentRows);
            }
        } catch (error) {
            console.error('Error fetching batch data:', error);
        } finally {
            setLoading(false);
        }
    };

    const addPaymentRow = () => {
        const today = new Date();
        setPayments([...payments, {
            id: `temp-${Date.now()}-${Math.random()}`,
            tax_category_id: '',
            deadline: today.toISOString().split('T')[0],
            amount: '0',
            tags: []
        }]);
    };

    const removePaymentRow = (index: number) => {
        const row = payments[index];
        if (row.payment_transaction_id) {
            alert(t('taxes.cannotRemoveLinkedPayment', 'finance'));
            return;
        }
        setPayments(payments.filter((_, i) => i !== index));
    };

    const updatePaymentRow = (index: number, field: keyof PaymentRow, value: any) => {
        const newPayments = [...payments];
        newPayments[index] = { ...newPayments[index], [field]: value };
        setPayments(newPayments);
    };

    const getTaxCategoryById = (id: string): TaxCategory | undefined => {
        return taxCategories.find(tc => tc.id === id);
    };

    const calculateTotal = (): number => {
        return payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
    };

    const handleSave = async () => {
        if (!companyId) {
            alert(t('taxes.validation.selectCompany', 'finance'));
            return;
        }

        if (payments.length === 0) {
            alert(t('taxes.validation.addPayment', 'finance'));
            return;
        }

        for (const payment of payments) {
            if (!payment.tax_category_id || !payment.deadline) {
                alert(t('taxes.validation.categoryDeadline', 'finance'));
                return;
            }
            if (!payment.amount || parseFloat(payment.amount) <= 0) {
                alert(t('taxes.validation.amounts', 'finance'));
                return;
            }
        }

        setSaving(true);
        try {
            const payload = {
                company_id: companyId,
                year,
                month,
                notes: notes || null,
                payments: payments.map(p => ({
                    tax_category_id: p.tax_category_id,
                    deadline: p.deadline,
                    amount: parseFloat(p.amount),
                    tag_ids: p.tags.map(t => t.id)
                }))
            };

            const url = batchId
                ? `/api/finance/tax-batches/${batchId}`
                : '/api/finance/tax-batches/';

            const method = batchId ? 'PUT' : 'POST';

            const response = await apiFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                onSave();
                onClose();
            } else {
                const error = await response.json();
                alert(t('taxes.saveFailed', 'finance', { error: error.detail || t('taxes.saveFailedFallback', 'finance') }));
            }
        } catch (error) {
            console.error('Error saving tax batch:', error);
            alert(t('taxes.saveFailedFallback', 'finance'));
        } finally {
            setSaving(false);
        }
    };

    const getCompanyById = (id: string): Company | undefined => {
        return companies.find(c => c.id === id);
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-6xl max-h-[90vh] ">
                <DialogHeader>
                    <DialogTitle className="text-2xl">
                        {batchId ? t('taxes.editBatch', 'finance') : t('taxes.newBatch', 'finance')}
                    </DialogTitle>
                </DialogHeader>

                {loading ? (
                    <div className="text-center py-8">{t('actions.loading', 'common')}</div>
                ) : (
                    <div className="space-y-6">
                        {/* Batch Details */}
                        <div className="grid grid-cols-4 gap-4">
                            <div>
                                <Label htmlFor="year">{t('time.year', 'common')}</Label>
                                <Select
                                    value={year.toString()}
                                    onValueChange={(value) => setYear(parseInt(value))}
                                    disabled={!!batchId}
                                >
                                    <SelectTrigger id="year">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {[2024, 2025, 2026, 2027].map(y => (
                                            <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <Label htmlFor="month">{t('time.month', 'common')}</Label>
                                <Select
                                    value={month.toString()}
                                    onValueChange={(value) => setMonth(parseInt(value))}
                                    disabled={!!batchId}
                                >
                                    <SelectTrigger id="month">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                                            <SelectItem key={m} value={m.toString()}>
                                                {new Date(2000, m - 1).toLocaleString('default', { month: 'long' })}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="col-span-2">
                                <Label htmlFor="company">{t('common.company', 'common')}</Label>
                                <Select
                                    value={companyId}
                                    onValueChange={setCompanyId}
                                    disabled={!!batchId}
                                >
                                    <SelectTrigger id="company">
                                        <SelectValue placeholder={t('taxes.selectCompany', 'finance')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {companies.map(company => (
                                            <SelectItem key={company.id} value={company.id}>
                                                {company.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div>
                            <Label htmlFor="notes">{t('forms.notes', 'common')}</Label>
                            <Input
                                id="notes"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder={t('taxes.optionalNotes', 'finance')}
                            />
                        </div>

                        {/* Payments Table */}
                        <div>
                            <div className="flex justify-between items-center mb-2">
                                <Label className="text-lg font-semibold mb-2 block">{t('units.amount', 'common')}</Label>
                            </div>

                            <div className="border rounded-lg">
                                <table className="w-full">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="p-2 text-left text-sm font-medium">{t('taxes.category', 'finance')}</th>
                                            <th className="p-2 text-left text-sm font-medium">{t('taxes.deadline', 'finance')}</th>
                                            <th className="p-2 text-left text-sm font-medium">{t('tags.tags', 'common')}</th>
                                            <th className="p-2 text-right text-sm font-medium">{t('units.amount', 'common')}</th>

                                            <th className="p-2 text-left text-sm font-medium">{t('taxes.paidLabel', 'finance')}</th>
                                            <th className="p-2 text-left text-sm font-medium"></th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {payments.length === 0 ? (
                                            <tr>
                                                <td colSpan={6} className="px-3 py-8 text-center text-gray-500">
                                                    {t('taxes.noPayments', 'finance')}
                                                </td>
                                            </tr>
                                        ) : (
                                            payments.map((payment, index) => (
                                                <tr key={payment.id || index} className="hover:bg-gray-50">
                                                    <td className="p-2">
                                                        <Select
                                                            value={payment.tax_category_id}
                                                            onValueChange={(value) => updatePaymentRow(index, 'tax_category_id', value)}
                                                        >
                                                            <SelectTrigger className="w-72 max-w-72">
                                                                <SelectValue placeholder={t('taxes.selectCategory', 'finance')} />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {taxCategories.map(tc => (
                                                                    <SelectItem key={tc.id} value={tc.id}>
                                                                        {tc.code} - {tc.name}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    </td>
                                                    <td className="px-3 py-2">
                                                        <Input
                                                            type="date"
                                                            value={payment.deadline}
                                                            onChange={(e) => updatePaymentRow(index, 'deadline', e.target.value)}
                                                            className="w-full"
                                                        />
                                                    </td>
                                                    <td className="p-2">
                                                        <div className="w-full z-100">
                                                            <TagSelector
                                                                selectedTags={payment.tags}
                                                                onChange={(tags) => updatePaymentRow(index, 'tags', tags)}
                                                                entityType="tax_payments"
                                                                entityId={payment.id || ''}
                                                                allowCreate={true}
                                                                localOnly={!payment.id || payment.id.startsWith('temp-')}
                                                            />
                                                        </div>
                                                    </td>
                                                    <td className="px-3 py-2">
                                                        <Input
                                                            type="number"
                                                            value={payment.amount}
                                                            onChange={(e) => updatePaymentRow(index, 'amount', e.target.value)}
                                                            className="w-full text-right"
                                                            step="0.01"
                                                        />
                                                    </td>

                                                    <td className="px-3 py-2">
                                                        {payment.payment_transaction_id && (
                                                            <a
                                                                href={`/payments?search=${payment.payment_transaction_id}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                                            >
                                                                <ExternalLink className="h-3 w-3" />
                                                                {t('actions.view', 'common')}
                                                            </a>
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-2 text-center">
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => removePaymentRow(index)}
                                                            disabled={!!payment.payment_transaction_id}
                                                            title={payment.payment_transaction_id ? t('taxes.cannotRemoveLinkedPayment', 'finance') : t('taxes.removePayment', 'finance')}
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>

                                </table>
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={addPaymentRow}
                                className="mt-2"
                            >
                                <Plus className="h-4 w-4 mr-1" />
                                {t('taxes.addPayment', 'finance')}
                            </Button>
                        </div>
                        <div className="flex justify-end">
                            <div className="text-lg font-semibold">
                                {t('general.total', 'finance')} {formatAmount(calculateTotal(), { currencyCode: baseCurrency })}
                            </div>
                        </div>

                        {/* Footer Actions */}
                        <div className="flex justify-end gap-2 pt-4 border-t">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={onClose}
                                disabled={saving}
                            >
                                {t('actions.cancel', 'common')}
                            </Button>
                            <Button
                                type="button"
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? t('actions.saving', 'common') : (batchId ? t('taxes.updateBatch', 'finance') : t('taxes.createBatch', 'finance'))}
                            </Button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
